import type React from "react";
import {
  Home,
  Package,
  Truck,
  BarChart3,
  LogOut,
  Building2,
  ShoppingCart,
  FileText,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import logo from "@/assets/logo.svg";
import { OrgType, useAuthService } from "../auth/AuthProvider";

// Navigation item interface
interface NavItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

// Navigation configuration for different account types
const navigationConfig: Record<OrgType | "default", NavItem[]> = {
  Receiver: [
    { label: "Dashboard", href: "/receiver", icon: Home },
    { label: "Loads", href: "/receiver/loads", icon: ShoppingCart },
    { label: "Claims", href: "/receiver/claims", icon: FileText },
    { label: "Invoicing", href: "/receiver/invoicing", icon: Package },
  ],
  Broker: [
    { label: "Dashboard", href: "/broker", icon: Home },
    { label: "Carriers", href: "/broker/carriers", icon: Building2 },
    { label: "Analytics", href: "/broker/analytics", icon: BarChart3 },
    { label: "Claims", href: "/broker/claims", icon: FileText },
    { label: "Invoicing", href: "/broker/invoicing", icon: Package },
  ],
  Shipper: [{ label: "Dashboard", href: "/shipper", icon: Home }],
  default: [
    { label: "Carrier Signup", href: "/carrier-signup", icon: Truck },
    { label: "Shipper Signup", href: "/shipper-signup", icon: Package },
    { label: "Receiver Signup", href: "/receiver-signup", icon: Package },
  ],
};

interface VerticalNavProps {}

export default function Navbar({}: VerticalNavProps) {
  const { authState, logout } = useAuthService();
  const navigate = useNavigate();

  const navItems = navigationConfig[authState?.orgType ?? "default"];

  const handleSignOut = async () => {
    await logout();
    navigate("/receiver-signup");
  };

  return (
    <div className="group fixed left-0 top-0 z-50 flex h-screen w-16 hover:w-64 flex-col bg-background border-r shadow-lg transition-all duration-300 ease-in-out">
      {/* Logo Section */}
      <div className="flex h-16 items-center px-3 group-hover:px-6 transition-all duration-300">
        <div className="flex items-center space-x-2">
          <div className="flex h-8 w-8 items-center justify-center rounded flex-shrink-0">
            {/* <Icon className="h-5 w-5 text-primary-foreground" /> */}
            <img src={logo} alt="Logo" className="" />
          </div>
          <span className="text-lg font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap overflow-hidden">
            <span className="text-lupurple-dark">Freight</span> | Demo
          </span>
        </div>
      </div>
      <Separator />
      {/* Navigation Links */}
      <nav className="flex-1 space-y-1 px-2 group-hover:px-3 transition-all duration-300 pt-4">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname === item.href;

          return (
            <div
              key={item.href}
              className={cn(
                "group-hover:cursor-pointer flex items-center space-x-3 rounded-lg px-2 group-hover:px-3 py-2 text-sm font-medium transition-all duration-300 hover:bg-lupurple-lighter hover:text-accent-foreground",
                isActive
                  ? "bg-lupurple-lighter text-accent-foreground"
                  : "text-muted-foreground"
              )}
              title={item.label}
              onClick={() => navigate(item.href)}
            >
              <Icon className="h-5 w-5 flex-shrink-0 ms-1" />
              <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap overflow-hidden">
                {item.label}
              </span>
            </div>
          );
        })}
      </nav>{" "}
      <Separator />
      {/* Sign Out Section */}
      <div className="p-2 group-hover:p-3 transition-all duration-300">
        <Button
          variant="ghost"
          className="w-full justify-center group-hover:justify-start text-muted-foreground hover:text-foreground px-2 group-hover:px-3 transition-all duration-300"
          onClick={handleSignOut}
          title="Sign Out"
        >
          <LogOut className="h-4 w-4 flex-shrink-0" />
          <span className="ml-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap overflow-hidden">
            Sign Out
          </span>
        </Button>
      </div>
    </div>
  );
}
